import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { canvasService } from '../../apiServices/CanvasService';
import { useModal } from '../../context/Modal/useModal';
import { useToast } from '../../context/Toast/useToast';
import { useUser } from '../../context/User/useUser';
import { useRoomId } from '../../hooks/useRoomId';
import { SubscriptionModal } from '../../pages/Subscription/SubscriptionModal';
import Button, { ButtonType } from '../Button/Button';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';

const ExportContainer = styled.div`
  display: flex;
  gap: 4px;
`;

function sanitizeFilename(input: string): string {
  // Replace invalid characters with an underscore or a dash
  const sanitized = input
    // eslint-disable-next-line no-control-regex
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_') // Replace invalid characters with underscores
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .replace(/[\x7F-\x9F]/g, '') // Remove control characters
    .replace(/\.+$/g, ''); // Remove trailing periods

  // Limit the length of the filename to avoid issues
  const maxLength = 255;
  return sanitized.substring(0, maxLength);
}

function openPdfFile(blob: Blob, name: string) {
  // Create a URL for the blob
  const url = window.URL.createObjectURL(blob);

  // Create a temporary link element
  const link = document.createElement('a');
  link.href = url;
  link.download = sanitizeFilename(name + '.pdf'); // Set the desired file name

  // Append the link to the document body (not visible to the user)
  document.body.appendChild(link);

  // Programmatically click the link to trigger the download
  link.click();

  // Clean up by removing the link and revoking the object URL
  link.remove();
  window.URL.revokeObjectURL(url);
}

export const ExportButton = () => {
  const user = useUser();
  const canvasId = useRoomId();
  const canvas = user.canvases.find((c) => c.canvasId === canvasId);
  const toast = useToast();
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);

  const { onOpen } = useModal();

  const openSubscriptionModal = () => {
    onOpen(<SubscriptionModal showDescription />);
  };
  const onGeneratePdf = async () => {
    if (!user.isSubscribed) {
      openSubscriptionModal();
      return;
    }

    setIsLoading(true);
    return canvasService
      .exportCanvas(canvasId)
      .then((blob) => {
        openPdfFile(blob, canvas?.name || '');
        toast.addToast({
          message: intl.formatMessage({ id: 'export.successToast' }),
          type: 'success',
        });
      })
      .catch(() => {
        toast.addToast({
          message: intl.formatMessage({ id: 'export.errorToast' }),
          type: 'error',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  return (
    <ExportContainer>
      {!user.isSubscribed && (
        <Button type={ButtonType.PrimaryLink} onClick={openSubscriptionModal}>
          <FormattedMessage id="export.subscribeButton" />
        </Button>
      )}
      <Button
        type={ButtonType.Primary}
        onClick={onGeneratePdf}
        fill
        disabled={isLoading}
      >
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <FormattedMessage id="export.exportButton" />
        )}
      </Button>
    </ExportContainer>
  );
};
